import React, { useState, useEffect } from 'react';
import { Palette, Sparkles, Move } from 'lucide-react';
import { colorSpectrum } from '../data/mock';

const SwatchGrid = ({ activeColor, onSelect, onPause, onResume }) => (
  <div
    className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-3 mb-14"
    onMouseEnter={onPause}
    onMouseLeave={onResume}
  >
    {colorSpectrum.map((c, idx) => {
      const isActive = activeColor.hex === c.hex;
      return (
        <button
          key={c.hex}
          onMouseEnter={() => onSelect(c, idx)}
          className={`group relative aspect-square rounded-2xl shadow-sm border border-white overflow-hidden btn-transition ${
            isActive ? 'scale-110 z-10 shadow-2xl' : 'hover:scale-105'
          }`}
          style={{ backgroundColor: c.hex }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
          <span
            className={`absolute bottom-2 left-2 text-[9px] font-bold font-mono tracking-tight transition-opacity ${
              isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            }`}
            style={{ color: '#ffffff', mixBlendMode: 'difference' }}
          >
            {c.hex}
          </span>
        </button>
      );
    })}
  </div>
);

const ActiveColorDetail = ({ activeColor }) => (
  <div
    className="relative grid md:grid-cols-12 items-center gap-8 rounded-3xl p-8 md:p-12 border border-slate-100 shadow-xl overflow-hidden transition-colors duration-700"
    style={{ backgroundColor: '#ffffff' }}
  >
    {/* Bar */}
    <div
      className="md:col-span-5 relative h-48 md:h-64 rounded-2xl overflow-hidden flex items-end p-6"
      style={{ backgroundColor: activeColor.hex }}
    >
      <div className="absolute inset-0 grain-overlay opacity-40" />
      <div className="relative text-white/90 font-mono" style={{ mixBlendMode: 'difference' }}>
        <p className="text-xs uppercase tracking-widest opacity-80">Selected Color</p>
        <p className="text-3xl font-black tracking-tight">{activeColor.name}</p>
      </div>
    </div>

    {/* Specs */}
    <div className="md:col-span-7">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-1">HEX</p>
          <p className="text-xl font-mono font-bold text-[#050F2E]">{activeColor.hex}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-1">LAB</p>
          <p className="text-xl font-mono font-bold text-[#050F2E]">{activeColor.lab}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-400 font-bold mb-1">Accuracy</p>
          <p className="text-xl font-mono font-bold text-[#050F2E]">ΔE &lt; 1.0</p>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3 text-sm text-slate-500">
        <Move className="h-4 w-4" />
        <span>Hover any swatch above to inspect. Cycling every 1.8s automatically.</span>
      </div>

      <div className="mt-6 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-[#FF4E50]" />
        <span className="text-sm font-semibold text-[#0A2A63]">Calibrated using Barbieri Spectro qb — ISO 13655 (M0-M1-M2-M3)</span>
      </div>
    </div>
  </div>
);

const ColorShowcase = () => {
  const [activeColor, setActiveColor] = useState(colorSpectrum[0]);
  const [paused, setPaused] = useState(false);

  // Auto cycle when not hovered
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => {
      setActiveColor((current) => {
        const idx = colorSpectrum.findIndex((c) => c.hex === current.hex);
        return colorSpectrum[(idx + 1) % colorSpectrum.length];
      });
    }, 1800);
    return () => clearInterval(t);
  }, [paused]);

  return (
    <section
      className="relative py-28 overflow-hidden"
      data-testid="color-showcase-section"
      style={{
        background:
          'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%),' +
          'radial-gradient(ellipse at 20% 20%, rgba(0,183,199,0.08), transparent 55%),' +
          'radial-gradient(ellipse at 80% 80%, rgba(229,0,126,0.06), transparent 55%)',
      }}
    >
      {/* Ambient color gradients */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 15% 20%, rgba(0,183,199,0.12), transparent 45%),' +
            'radial-gradient(circle at 85% 25%, rgba(229,0,126,0.10), transparent 45%),' +
            'radial-gradient(circle at 25% 85%, rgba(255,215,0,0.10), transparent 45%),' +
            'radial-gradient(circle at 85% 90%, rgba(15,123,63,0.08), transparent 45%)',
        }}
      />

      {/* Background dot pattern */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #0A2A63 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="container-pn relative">
        {/* Heading */}
        <div className="max-w-4xl mb-14">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3 inline-flex items-center gap-2">
            <Palette className="h-3.5 w-3.5" /> Precise Color Evolution
          </p>
          <h2 className="text-4xl md:text-6xl font-black text-[#050F2E] tracking-tight leading-[1.03]">
            There are more than{' '}
            <span className="relative inline-block">
              <span
                className="absolute -inset-2 rounded-2xl opacity-25 blur-md transition-colors duration-500"
                style={{ backgroundColor: activeColor.hex }}
              />
              <span className="relative transition-colors duration-500" style={{ color: activeColor.hex }}>
                10 million colors
              </span>
            </span>
            ,<br />
            our job? Reflect every single one.
          </h2>
          <p className="mt-6 text-lg text-slate-600 max-w-2xl">
            We are moved by the colors of the world and work to reflect them across the most
            diverse print formats. Hover any swatch below to feel the spectrum.
          </p>
        </div>

        <SwatchGrid
          activeColor={activeColor}
          onSelect={(c) => setActiveColor(c)}
          onPause={() => setPaused(true)}
          onResume={() => setPaused(false)}
        />

        <ActiveColorDetail activeColor={activeColor} />
      </div>
    </section>
  );
};

export default ColorShowcase;
